import { Building2, Home, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const COMMERCIAL_IMG =
  "https://images.unsplash.com/photo-1758498231185-2386de2723af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvciUyMGNvbW1lcmNpYWwlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODE5NDg0ODl8MA&ixlib=rb-4.1.0&q=85";
const RESIDENTIAL_IMG = "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const services = [
  {
    icon: Building2,
    tag: "Commercial",
    title: "Commercial Renovations",
    body: "Build-outs and refreshes that keep your business moving. We renovate offices, retail spaces, and restaurants with durable, code-compliant results.",
    img: COMMERCIAL_IMG,
    points: ["Offices & workspaces", "Retail & storefronts", "Restaurants & hospitality"],
  },
  {
    icon: Home,
    tag: "Residential",
    title: "House Renovations",
    body: "Transform the spaces you live in. From kitchens and baths to full-home remodels and additions, we deliver finishes that feel custom and last for years.",
    img: RESIDENTIAL_IMG,
    points: ["Kitchens & bathrooms", "Full-home remodels", "Additions & extensions"],
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 md:py-36 bg-brand-footer" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <Reveal>
              <Overline>What We Do</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-brand-text max-w-2xl leading-[1.02]">
                Two disciplines. One standard of quality.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-brand-muted max-w-sm">
              Whatever the space, we bring the same craftsmanship and care from
              demolition to final walkthrough.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={0.08 * i}>
              <div className="group h-full flex flex-col border border-brand-hi rounded-sm bg-brand-surface overflow-hidden transition-colors hover:border-brand-orange/50" data-testid={`service-card-${s.tag.toLowerCase()}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-brand-surface/20 to-transparent" />
                  <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-brand-navy/80 backdrop-blur text-brand-orange text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm border border-brand-hi">
                    <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {s.tag}
                  </span>
                </div>

                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-heading text-2xl md:text-3xl font-semibold text-brand-text">{s.title}</h3>
                    <ArrowUpRight className="w-6 h-6 text-brand-muted group-hover:text-brand-orange transition-colors shrink-0" strokeWidth={1.5} />
                  </div>
                  <p className="mt-4 text-brand-muted leading-relaxed">{s.body}</p>
                  <ul className="mt-6 space-y-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-brand-text/90">
                        <Check className="w-4 h-4 text-brand-orange shrink-0" strokeWidth={2} />
                        <span className="text-sm md:text-base">{p}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:text-brand-orange transition-colors"
                    data-testid={`service-cta-${s.tag.toLowerCase()}`}
                  >
                    Start a {s.tag.toLowerCase()} project
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
